import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { useAction, useMutation, useQuery } from "convex/react";
import {
  ArrowLeft,
  CheckCircle,
  Copy,
  CreditCard,
  Download,
  Heart,
  Link2,
  Lock,
  MessageSquare,
  Music2,
  Pause,
  Pin,
  Play,
  RotateCcw,
  Send,
} from "lucide-react";
import { api } from "../../convex/_generated/api";
import Waveform from "./Waveform";
import AudioMetaStrip from "./AudioMetaStrip";
import CommentList from "./CommentList";
import type { AudioMeta } from "../lib/audioMeta";
import { parseTitleHints } from "../lib/audioMeta";
import {
  formatTime,
  getDownloadUrl,
  getFileUrl,
  groupFiles,
  isAudioFormat,
  pickDefaultAudioFile,
} from "../lib/media";
import { downloadFilesAsZip } from "../lib/zipDownload";
import { isValidTrackIndex, suggestTrackIndex } from "../lib/bundleProject";
import {
  commentsForReviewFile,
  commentsForTrack,
} from "../lib/projectCommentScope";

type Mode = "admin" | "artist" | "guest";

interface Props {
  mode: Mode;
  projectId?: string;
  token?: string;
  onBack?: () => void;
}

const PROJECT_TYPE_LABELS: Record<string, string> = {
  beat: "Beat",
  mix_master: "Mix & Master",
  full_production: "Full Production",
  "3_track_bundle": "3-Track Bundle",
};

const TRACK_STATUS_COLORS: Record<string, string> = {
  in_progress: "#f59e0b",
  done: "#22c55e",
  approved: "#16bb1b",
  needs_revisions: "#ef4444",
};

const TRACK_STATUS_LABELS: Record<string, string> = {
  in_progress: "In Progress",
  done: "Done",
  approved: "Approved",
  needs_revisions: "Needs Revisions",
};

const COMMENT_CATEGORIES = [
  { value: "", label: "General" },
  { value: "mix_issue", label: "Mix Issue" },
  { value: "vocal_level", label: "Vocal Level" },
  { value: "timing", label: "Timing" },
  { value: "arrangement", label: "Arrangement" },
  { value: "other", label: "Other" },
];

type ReviewFile = {
  id: string;
  title: string;
  type: string;
  format: string;
  access: string;
  sourcePath?: string;
  storageProvider?: string;
  deliveryUrl?: string;
  driveFileId?: string;
  previewable?: boolean;
  trackIndex?: number;
  isMainMix?: boolean;
  isFavourite?: boolean;
};

type ReviewComment = {
  id: string;
  authorRole: string;
  fileId?: string;
  trackIndex?: number;
  timestampSec: number;
  text: string;
  status: string;
  category?: string;
  replyTo?: string;
  createdAt: number;
};

type ReviewTrack = {
  title: string;
  status: string;
  musicalKey?: string;
  bpm?: number;
  notes?: string;
};

type ReviewData = {
  projectId: string;
  title: string;
  status: string;
  artistName: string;
  projectType?: string;
  brief?: string;
  bpm?: number;
  musicalKey?: string;
  priceCents: number;
  totalPaidCents: number;
  balanceCents: number;
  pendingPaymentAmountCents?: number;
  canCheckout: boolean;
  canComment: boolean;
  canRequestChanges: boolean;
  canApprove: boolean;
  approved: boolean;
  canDownload: boolean;
  mainFileId?: string;
  favouriteFileId?: string;
  tracks?: ReviewTrack[];
  files: ReviewFile[];
  comments: ReviewComment[];
};

function statusLabel(status: string) {
  return status.replace(/_/g, " ").replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function guestMediaUrl(file: ReviewFile, token: string, purpose?: "download") {
  const params = new window.URLSearchParams({
    id: file.driveFileId ?? "",
    reviewToken: token,
  });
  if (purpose) params.set("purpose", purpose);
  return `/api/drive-download?${params.toString()}`;
}

export default function ProjectReview({ mode, projectId, token, onBack }: Props) {
  const isAdmin = mode === "admin";
  const isGuest = mode === "guest";

  // ── Data source ──────────────────────────────────────────────────
  const guestReview = useQuery(
    api.reviewLinks.getGuestReview,
    isGuest && token ? { token } : "skip",
  ) as ReviewData | null | undefined;

  const project = useQuery(
    api.queries.getProject,
    !isGuest && projectId ? { projectId: projectId as any } : "skip",
  ) as any;
  const comments = useQuery(
    api.projectComments.getProjectComments,
    !isGuest && projectId ? { projectId: projectId as any } : "skip",
  ) as ReviewComment[] | undefined;
  const paymentStatus = useQuery(
    api.queries.getProjectPayments,
    !isGuest && projectId ? { projectId: projectId as any } : "skip",
  ) as { totalPaidCents: number; hasPending: boolean } | undefined;

  // ── Mutations / actions ─────────────────────────────────────────
  const updateStatus = useMutation(api.projectStatus.updateProjectStatus);
  const approveProject = useMutation(api.projectStatus.approveProject);
  const requestTrackRevisions = useMutation(api.projectStatus.requestTrackRevisions);
  const updateTrackStatus = useMutation(api.projectStatus.updateTrackStatus);
  const assignFileToTrack = useMutation(api.mutations.assignFileToTrack);
  const addComment = useMutation(api.projectComments.addProjectComment);
  const setMainMix = useMutation(api.mutations.setMainMix);
  const setFavourite = useMutation(api.mutations.setFavourite);
  const createReviewLink = useMutation(api.reviewLinks.createReviewLink);
  const recordExternalPayment = useMutation(api.payments.recordExternalPayment);
  const deleteArtistFile = useMutation(api.mutations.deleteArtistFile);

  const createCheckout = useAction(api.payments.createGuestCheckoutSession);
  const createCheckoutSession = useAction(api.payments.createCheckoutSession);
  const requestChanges = useMutation(api.reviewLinks.requestChanges);
  const addGuestComment = useMutation(api.reviewLinks.addGuestComment);
  const approveGuestReview = useMutation(api.reviewLinks.approveGuestReview);

  // ── Normalize data into a single shape ────────────────────────────
  const data: ReviewData | null | undefined = useMemo(() => {
    if (isGuest) return guestReview;
    if (!project) return project; // undefined while loading, null if not found
    const priceCents = project.priceCents ?? 0;
    const totalPaidCents = paymentStatus?.totalPaidCents ?? 0;
    const balanceCents = Math.max(priceCents - totalPaidCents, 0);
    const status = project.status ?? "in_progress";
    const reviewOpen = status !== "completed" && status !== "delivered";
    const hasTracks = Boolean(project.tracks?.length);
    const allTracksApproved =
      hasTracks && project.tracks.every((t: any) => t.status === "approved");
    const isPaidInFull = priceCents <= 0 || totalPaidCents >= priceCents;
    const canDownload =
      isAdmin || (status === "completed" && isPaidInFull && (!hasTracks || allTracksApproved));
    const files: ReviewFile[] = (project.files ?? []).map((f: any) => ({
      id: f.id,
      title: f.title,
      type: f.type,
      format: f.format,
      access: f.access,
      sourcePath: f.sourcePath,
      storageProvider: f.storageProvider,
      deliveryUrl: f.deliveryUrl,
      trackIndex: f.trackIndex,
      isMainMix: f.isMainMix,
      isFavourite: f.isFavourite,
    }));
    return {
      projectId: project.id,
      title: project.title,
      status,
      artistName: project.artist?.stageName ?? project.artist?.name ?? "Artist",
      projectType: project.projectType,
      brief: project.brief,
      bpm: project.bpm,
      musicalKey: project.musicalKey,
      priceCents,
      totalPaidCents,
      balanceCents,
      canCheckout: false,
      canComment: reviewOpen,
      canRequestChanges: reviewOpen,
      canApprove: reviewOpen && (status === "ready_for_review" || status === "approved"),
      approved: status === "approved" || status === "completed",
      canDownload,
      mainFileId: project.mainFileId,
      favouriteFileId: project.favouriteFileId,
      tracks: project.tracks,
      files,
      comments: (comments ?? []) as ReviewComment[],
    };
  }, [isGuest, guestReview, project, paymentStatus, comments, isAdmin]);

  // ── Player state ─────────────────────────────────────────────────
  const audioRef = useRef<HTMLAudioElement>(null);
  const pendingSeekRef = useRef<number | null>(null);
  const [selectedFileId, setSelectedFileId] = useState<string | null>(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [fileMeta, setFileMeta] = useState<AudioMeta | null>(null);
  const [metaLoading, setMetaLoading] = useState(false);

  // ── Form / action state ──────────────────────────────────────────
  const [commentText, setCommentText] = useState("");
  const [commentTimestamp, setCommentTimestamp] = useState(0);
  const [commentCategory, setCommentCategory] = useState("");
  const [showCommentForm, setShowCommentForm] = useState(false);
  const [commentFileId, setCommentFileId] = useState<string | undefined>();
  const [commentTrackIndex, setCommentTrackIndex] = useState<number | undefined>();
  const [commentSubmitted, setCommentSubmitted] = useState(false);
  const [commentError, setCommentError] = useState("");
  const [commentLoading, setCommentLoading] = useState(false);
  const [showChangesForm, setShowChangesForm] = useState(false);
  const [changeMessage, setChangeMessage] = useState("");
  const [changeLoading, setChangeLoading] = useState(false);
  const [changeSubmitted, setChangeSubmitted] = useState(false);
  const [changeError, setChangeError] = useState("");
  const [approveLoading, setApproveLoading] = useState(false);
  const [approveError, setApproveError] = useState("");
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [statusUpdating, setStatusUpdating] = useState(false);
  const [statusError, setStatusError] = useState("");
  const [paymentUpdating, setPaymentUpdating] = useState(false);
  const [paymentError, setPaymentError] = useState("");
  const [pinningId, setPinningId] = useState<string | null>(null);
  const [favingId, setFavingId] = useState<string | null>(null);
  const [trackUpdating, setTrackUpdating] = useState<number | null>(null);
  const [assigningFileId, setAssigningFileId] = useState<string | null>(null);
  const [trackError, setTrackError] = useState("");
  const [zippingGroup, setZippingGroup] = useState<string | null>(null);
  const [collapsedGroups, setCollapsedGroups] = useState<Record<string, boolean>>({});
  const [reviewLinkUrl, setReviewLinkUrl] = useState("");
  const [reviewLinkLoading, setReviewLinkLoading] = useState(false);
  const [reviewLinkCopied, setReviewLinkCopied] = useState(false);
  const [reviewLinkError, setReviewLinkError] = useState("");

  const files = data?.files ?? [];
  const tracks = (data?.tracks ?? []) as ReviewTrack[];
  const hasTracks = tracks.length > 0;
  const projectComments = (data?.comments ?? []) as ReviewComment[];

  const defaultFile = useMemo(
    () => pickDefaultAudioFile(files, data?.mainFileId, data?.favouriteFileId),
    [files, data?.mainFileId, data?.favouriteFileId],
  );
  const activeFileId = selectedFileId ?? defaultFile?.id ?? null;
  const activeFile = files.find((f) => f.id === activeFileId) ?? defaultFile;
  const audioUrl = useMemo(() => {
    if (!activeFile) return undefined;
    if (isGuest && token) return guestMediaUrl(activeFile, token);
    return getFileUrl(activeFile);
  }, [activeFile, isGuest, token]);

  const titleHints = parseTitleHints(activeFile?.title);
  const displayBpm = data?.bpm ?? titleHints.bpm;
  const displayKey = data?.musicalKey ?? titleHints.key;

  // Reset on project change
  useEffect(() => {
    setSelectedFileId(null);
    setCurrentTime(0);
    setDuration(0);
    setIsPlaying(false);
    setFileMeta(null);
    setShowCommentForm(false);
    setCommentText("");
    setCommentTimestamp(0);
    setCommentFileId(undefined);
    setCommentTrackIndex(undefined);
    setReviewLinkUrl("");
    setReviewLinkCopied(false);
    setReviewLinkError("");
    setPaymentError("");
    setStatusError("");
    setApproveError("");
    setChangeError("");
    setChangeSubmitted(false);
    setShowChangesForm(false);
  }, [data?.projectId]);

  useEffect(() => {
    setFileMeta(null);
    setMetaLoading(Boolean(audioUrl));
  }, [audioUrl]);

  const handleAudioMeta = useCallback((meta: {
    durationSec?: number;
    sampleRate?: number;
    channels?: number;
    sizeBytes?: number;
    bitrateKbps?: number;
    mimeType?: string;
  }) => {
    setFileMeta({ ...meta, source: "probe" });
    setMetaLoading(false);
    if (meta.durationSec && Number.isFinite(meta.durationSec)) {
      setDuration(meta.durationSec);
    }
  }, []);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    function onTimeUpdate() { setCurrentTime(audio!.currentTime); }
    function onLoadedMetadata() {
      setDuration(audio!.duration || 0);
      if (pendingSeekRef.current !== null) {
        const nextTime = Math.min(
          pendingSeekRef.current,
          Number.isFinite(audio!.duration) ? audio!.duration : pendingSeekRef.current,
        );
        audio!.currentTime = nextTime;
        setCurrentTime(nextTime);
        pendingSeekRef.current = null;
      }
    }
    function onPlay() { setIsPlaying(true); }
    function onPause() { setIsPlaying(false); }
    function onEnded() { setIsPlaying(false); }
    audio.addEventListener("timeupdate", onTimeUpdate);
    audio.addEventListener("loadedmetadata", onLoadedMetadata);
    audio.addEventListener("play", onPlay);
    audio.addEventListener("pause", onPause);
    audio.addEventListener("ended", onEnded);
    return () => {
      audio.removeEventListener("timeupdate", onTimeUpdate);
      audio.removeEventListener("loadedmetadata", onLoadedMetadata);
      audio.removeEventListener("play", onPlay);
      audio.removeEventListener("pause", onPause);
      audio.removeEventListener("ended", onEnded);
    };
  }, [audioUrl]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || !audioUrl) return;
    audio.load();
    setCurrentTime(0);
    setIsPlaying(false);
  }, [audioUrl]);

  function handleSeek(time: number) {
    if (!audioRef.current) return;
    audioRef.current.currentTime = time;
    setCurrentTime(time);
  }

  function togglePlay() {
    if (!audioRef.current || !audioUrl) return;
    if (isPlaying) { audioRef.current.pause(); return; }
    void audioRef.current.play().catch(() => setIsPlaying(false));
  }

  function playFile(file: ReviewFile) {
    const url = isGuest && token ? guestMediaUrl(file, token) : getFileUrl(file);
    if (!url || !isAudioFormat(file.format, file.title)) return;
    if (file.access && file.access !== "available") return;
    const same = file.id === activeFileId;
    setSelectedFileId(file.id);
    if (same) { togglePlay(); return; }
    setCurrentTime(0);
    setDuration(0);
    window.requestAnimationFrame(() => {
      const audio = audioRef.current;
      if (!audio) return;
      void audio.play().catch(() => setIsPlaying(false));
    });
  }

  function startComment(timestampSec: number, file?: ReviewFile, trackIndex?: number) {
    setCommentTimestamp(Math.max(0, timestampSec));
    setCommentFileId(file?.id);
    setCommentTrackIndex(trackIndex);
    setCommentError("");
    setCommentSubmitted(false);
    setShowChangesForm(false);
    setShowCommentForm(true);
  }

  async function handleCommentSubmit(e: FormEvent) {
    e.preventDefault();
    if (!data) return;
    const text = commentText.trim();
    if (!text) { setCommentError("Write a comment before posting."); return; }
    setCommentError("");
    setCommentLoading(true);
    try {
      if (isGuest && token) {
        await addGuestComment({
          reviewToken: token,
          fileId: (commentFileId ?? activeFile?.id ?? files[0]?.id) as any,
          timestampSec: commentTimestamp,
          text,
          category: commentCategory || undefined,
        });
      } else if (projectId) {
        await addComment({
          projectId: projectId as any,
          artistId: (data as any).artistId ?? (project?.artistId as any),
          authorRole: isAdmin ? "admin" : "artist",
          ...(commentFileId ? { fileId: commentFileId as any } : {}),
          ...(commentTrackIndex !== undefined ? { trackIndex: commentTrackIndex } : {}),
          timestampSec: commentTimestamp,
          text,
          category: commentCategory || undefined,
        });
      }
      setCommentText("");
      setCommentCategory("");
      setShowCommentForm(false);
      setCommentSubmitted(true);
    } catch (error) {
      setCommentError(error instanceof Error ? error.message : "Could not post your comment");
    } finally {
      setCommentLoading(false);
    }
  }

  async function handleRequestChanges(e: FormEvent) {
    e.preventDefault();
    if (!data || !token) return;
    const message = changeMessage.trim();
    if (message.length < 3) { setChangeError("Tell us what you would like changed."); return; }
    setChangeError("");
    setChangeLoading(true);
    try {
      await requestChanges({ reviewToken: token, message });
      setChangeSubmitted(true);
      setShowChangesForm(false);
      setChangeMessage("");
    } catch (error) {
      setChangeError(error instanceof Error ? error.message : "Could not send your request");
    } finally {
      setChangeLoading(false);
    }
  }

  async function handleApproveAndPay() {
    if (!data) return;
    setApproveError("");
    setShowCommentForm(false);
    setShowChangesForm(false);
    setApproveLoading(true);
    setCheckoutLoading(true);
    try {
      if (isGuest && token) {
        if (data.canApprove) await approveGuestReview({ reviewToken: token });
        if (data.canCheckout) {
          const result = await createCheckout({ reviewToken: token, amountCents: data.balanceCents });
          if (result?.checkoutUrl) window.location.assign(result.checkoutUrl);
        }
      } else if (projectId) {
        await approveProject({ projectId: projectId as any });
        // Single approve-and-pay flow: after approval, send the artist straight
        // to Stripe when there is still a balance to pay (mirrors the guest path).
        if (data.balanceCents > 0) {
          const result = await createCheckoutSession({ projectId: projectId as any });
          if (result?.checkoutUrl) window.location.assign(result.checkoutUrl);
        }
      }
    } catch (error) {
      setApproveError(error instanceof Error ? error.message : "Could not approve this mix");
    } finally {
      setApproveLoading(false);
      setCheckoutLoading(false);
    }
  }

  async function handleStatusChange(newStatus: string) {
    if (!projectId) return;
    setStatusError("");
    setStatusUpdating(true);
    try {
      await updateStatus({ projectId: projectId as any, status: newStatus });
    } catch (err) {
      setStatusError(err instanceof Error ? err.message : "Could not update project status");
    } finally {
      setStatusUpdating(false);
    }
  }

  async function handleRecordExternalPayment() {
    if (!isAdmin || !projectId || !data) return;
    const amountCents = Math.max(data.balanceCents, 0);
    if (amountCents <= 0) return;
    const formattedAmount = new Intl.NumberFormat("en-AU", { currency: "AUD", style: "currency" }).format(amountCents / 100);
    if (!window.confirm(`Record the external payment of ${formattedAmount} and unlock the full files?`)) return;
    setPaymentError("");
    setPaymentUpdating(true);
    try {
      await recordExternalPayment({ projectId: projectId as any });
    } catch (err) {
      setPaymentError(err instanceof Error ? err.message : "Could not record external payment");
    } finally {
      setPaymentUpdating(false);
    }
  }

  async function handleCreateReviewLink() {
    if (!projectId) return;
    setReviewLinkError("");
    setReviewLinkCopied(false);
    setReviewLinkLoading(true);
    try {
      const result = await createReviewLink({ projectId: projectId as any });
      setReviewLinkUrl(result.url);
      try { await navigator.clipboard?.writeText(result.url); setReviewLinkCopied(true); } catch { /* ignore */ }
    } catch (err) {
      setReviewLinkError(err instanceof Error ? err.message : "Could not create guest review link");
    } finally {
      setReviewLinkLoading(false);
    }
  }

  async function handleCopyReviewLink() {
    if (!reviewLinkUrl) return;
    try { await navigator.clipboard.writeText(reviewLinkUrl); setReviewLinkCopied(true); } catch { setReviewLinkCopied(false); }
  }

  async function handlePinMainMix(file: ReviewFile) {
    if (!isAdmin || !projectId) return;
    setPinningId(file.id);
    try { await setMainMix({ projectId: projectId as any, fileId: file.id as any }); setSelectedFileId(file.id); }
    catch (err) { console.error(err); } finally { setPinningId(null); }
  }

  async function handleFavourite(file: ReviewFile) {
    if (!projectId) return;
    setFavingId(file.id);
    try { await setFavourite({ projectId: projectId as any, fileId: file.id as any }); setSelectedFileId(file.id); }
    catch (err) { console.error(err); } finally { setFavingId(null); }
  }

  async function handleDelete(fileId: string) {
    await deleteArtistFile({ fileId: fileId as any });
  }

  async function handleZipGroup(groupLabel: string, groupFilesList: ReviewFile[]) {
    setZippingGroup(groupLabel);
    try {
      const slug = groupLabel.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
      const projectSlug = (data?.title || "project").toLowerCase().replace(/[^a-z0-9]+/g, "-");
      await downloadFilesAsZip(groupFilesList, `${projectSlug}-${slug}.zip`);
    } catch (err) {
      console.error(err);
      window.alert(err instanceof Error ? err.message : "Could not create ZIP");
    } finally {
      setZippingGroup(null);
    }
  }

  async function handleTrackAction(trackIndex: number, action: () => Promise<unknown>) {
    setTrackError("");
    setTrackUpdating(trackIndex);
    try { await action(); }
    catch (err) { setTrackError(err instanceof Error ? err.message : "Could not update track"); }
    finally { setTrackUpdating(null); }
  }

  async function handleFileAssignment(fileId: string, trackIndex: number | undefined) {
    if (!projectId) return;
    setTrackError("");
    setAssigningFileId(fileId);
    try { await assignFileToTrack({ projectId: projectId as any, fileId: fileId as any, trackIndex }); }
    catch (err) { setTrackError(err instanceof Error ? err.message : "Could not assign file"); }
    finally { setAssigningFileId(null); }
  }

  function handleSelectComment(comment: { fileId?: string; timestampSec: number }) {
    if (comment.fileId && comment.fileId !== activeFileId) {
      const file = files.find((candidate) => candidate.id === comment.fileId);
      if (file) { pendingSeekRef.current = comment.timestampSec; setSelectedFileId(file.id); return; }
    }
    handleSeek(comment.timestampSec);
  }

  // ── Loading / error states ───────────────────────────────────────
  if (data === undefined) {
    return (
      <div className="guest-review-shell">
        <div className="guest-review-card guest-review-loading">
          <div className="spinner" />
          <p>Loading project…</p>
        </div>
      </div>
    );
  }

  if (data === null) {
    return (
      <div className="guest-review-shell">
        <div className="guest-review-card guest-review-error">
          <Lock className="h-8 w-8" />
          <h1>Project unavailable</h1>
          <p>This project is not available or you do not have access to it.</p>
          {onBack ? (
            <button className="primary-link" onClick={onBack} type="button">
              <ArrowLeft className="h-4 w-4" /> Back
            </button>
          ) : (
            <a className="primary-link" href="/">Open the artist portal</a>
          )}
        </div>
      </div>
    );
  }

  const status = data.status;
  const isDelivered = status === "delivered";
  const isCompleted = status === "completed";
  const isClosed = isCompleted || isDelivered;
  const reviewEnabled = !isClosed;
  const priceCents = data.priceCents;
  const totalPaidCents = data.totalPaidCents;
  const balanceCents = data.balanceCents;
  const isPaidInFull = priceCents <= 0 || totalPaidCents >= priceCents;
  const paymentSatisfied = priceCents <= 0 || isPaidInFull;
  const canDownload = data.canDownload;
  const canApprove = data.canApprove;
  const canRequestChanges = data.canRequestChanges;
  const canComment = data.canComment;
  const showPayment = status === "approved" || status === "payment_required" || isDelivered;
  const activeIsMainMix = activeFile?.id === data.mainFileId || activeFile?.isMainMix === true;
  const openCommentCount = projectComments.filter((c) => !c.replyTo && c.status === "open").length;
  const approvedTrackCount = tracks.filter((t) => t.status === "approved").length;
  const allTracksApproved = hasTracks && tracks.every((t) => t.status === "approved");
  const canDownloadBundle = isAdmin || (isCompleted && allTracksApproved && paymentSatisfied);
  const fileLabels = Object.fromEntries(files.map((f) => [f.id, f.title]));
  const commentMarkersFor = (fileId: string | undefined, trackIndex?: number) =>
    reviewEnabled
      ? commentsForReviewFile(projectComments, fileId, trackIndex)
          .filter((c) => !c.replyTo)
          .map((c) => ({ id: c.id, timestampSec: c.timestampSec, status: c.status, authorRole: c.authorRole, text: c.text }))
      : [];
  const activeFileComments = commentsForReviewFile(projectComments, activeFileId ?? undefined);
  const grouped = groupFiles(files);
  const trackFileMap = tracks.map((track, index) => ({
    track, index,
    trackFiles: files.filter((f) => f.trackIndex === index),
  }));
  const unassignedFiles = hasTracks ? files.filter((f) => !isValidTrackIndex(f.trackIndex, tracks.length)) : [];
  const GUEST_REVIEW_STATUSES = ["in_progress", "ready_for_review", "revisions", "approved", "payment_required", "completed", "delivered"];
  const canCreateReviewLink = isAdmin && GUEST_REVIEW_STATUSES.includes(status);

  function renderCommentForm(contextLabel: string) {
    return (
      <form className="comment-form soundcloud-comment-form" onSubmit={handleCommentSubmit}>
        <div className="comment-form-header">
          <span>{commentTimestamp > 0 ? `${contextLabel} at ${formatTime(commentTimestamp)}` : contextLabel}</span>
          <button className="text-button" onClick={() => setShowCommentForm(false)} type="button">Cancel</button>
        </div>
        <textarea autoFocus onChange={(e) => setCommentText(e.target.value)} placeholder="Share precise feedback for this moment…" rows={2} value={commentText} />
        <div className="comment-form-bottom">
          <select aria-label="Comment category" onChange={(e) => setCommentCategory(e.target.value)} value={commentCategory}>
            {COMMENT_CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
          <button className="primary-action" disabled={!commentText.trim()} type="submit">
            <Send className="h-4 w-4" /> Post comment
          </button>
        </div>
      </form>
    );
  }

  const previewSection = activeFile && audioUrl ? (
    <section aria-labelledby="project-preview-heading" className="guest-review-card guest-player-card guest-preview-card">
      <div className="guest-player-heading">
        <div>
          <p className="eyebrow" id="project-preview-heading">
            <Music2 className="h-4 w-4" />
            {activeIsMainMix ? "Main mix" : "Track preview"}
          </p>
          <h2>{activeFile.title}</h2>
          <small>{activeFile.format.toUpperCase()} · {formatTime(currentTime)} / {formatTime(duration)}</small>
        </div>
      </div>
      <div className="soundcloud-player guest-soundcloud-player">
        <button
          aria-label={`${isPlaying ? "Pause" : "Play"} ${activeFile.title}`}
          className={`soundcloud-play-button ${isPlaying ? "playing" : ""}`}
          onClick={togglePlay}
          title={isPlaying ? "Pause track" : "Play track"}
          type="button"
        >
          {isPlaying ? <Pause className="h-5 w-5" /> : <Play className="h-5 w-5" />}
        </button>
        <div className="soundcloud-player-content">
          <audio ref={audioRef} preload="metadata" src={audioUrl} />
          <AudioMetaStrip bpm={displayBpm} musicalKey={displayKey} format={activeFile.format} meta={fileMeta} loading={metaLoading && !fileMeta} />
          <Waveform
            audioUrl={audioUrl}
            comments={commentMarkersFor(activeFileId ?? undefined)}
            currentTime={currentTime}
            duration={duration}
            height={128}
            isPlaying={isPlaying}
            onAddComment={reviewEnabled && canComment ? (ts) => startComment(ts, activeFile) : undefined}
            onMeta={handleAudioMeta}
            onSeek={handleSeek}
          />
        </div>
      </div>

      <div className="guest-player-review-console">
        <div aria-label="Review actions" className="guest-player-action-bar">
          {canComment ? (
            <button className="guest-secondary-action" onClick={() => startComment(currentTime, activeFile)} type="button">
              <MessageSquare className="h-4 w-4" /> Comment at {formatTime(currentTime)}
            </button>
          ) : null}
          {canRequestChanges && isGuest ? (
            <button className="guest-secondary-action" onClick={() => { setChangeError(""); setChangeSubmitted(false); setShowCommentForm(false); setShowChangesForm((v) => !v); }} type="button">
              <MessageSquare className="h-4 w-4" /> Request changes
            </button>
          ) : null}
          {!isGuest && canRequestChanges ? (
            <button className="guest-secondary-action" onClick={() => void handleApproveAndPay()} type="button">
              <RotateCcw className="h-4 w-4" /> Request Revisions
            </button>
          ) : null}
          {canApprove ? (
            <button className="guest-approve-action" disabled={approveLoading || checkoutLoading} onClick={() => void handleApproveAndPay()} type="button">
              {isGuest && data.canCheckout ? <CreditCard className="h-4 w-4" /> : <CheckCircle className="h-4 w-4" />}
              {approveLoading || checkoutLoading
                ? isGuest && data.canCheckout ? "Opening secure checkout…" : "Approving…"
                : isGuest && data.canCheckout
                  ? data.pendingPaymentAmountCents ? "Approve & continue payment" : `Approve & pay $${(balanceCents / 100).toFixed(2)}`
                  : isGuest && data.pendingPaymentAmountCents ? "Continue payment"
                  : isGuest ? `Pay $${(balanceCents / 100).toFixed(2)}`
                  : data.balanceCents > 0 ? `Approve & pay $${(balanceCents / 100).toFixed(2)}`
                  : "Approve Project"}
            </button>
          ) : data.approved ? (
            <span className="guest-action-state approved"><CheckCircle className="h-4 w-4" /> Approved</span>
          ) : null}
          {canDownload ? (
            <a className="guest-download-action" download href={isGuest && token ? guestMediaUrl(activeFile, token, "download") : getDownloadUrl(activeFile)} rel="noreferrer">
              <Download className="h-4 w-4" /> Download mix
            </a>
          ) : priceCents > 0 && balanceCents <= 0 ? (
            <span className="guest-action-state paid"><CheckCircle className="h-4 w-4" /> Paid · approval unlocks download</span>
          ) : null}
        </div>

        {approveError ? <p className="form-error">{approveError}</p> : null}
        {commentSubmitted ? (
          <div className="guest-request-success"><CheckCircle className="h-4 w-4" /> Comment posted at {formatTime(commentTimestamp)}.</div>
        ) : null}

        {showCommentForm ? (
          <form className="guest-inline-review-form" onSubmit={handleCommentSubmit}>
            <div className="guest-inline-form-heading">
              <strong>Comment at {formatTime(commentTimestamp)}</strong>
              <button className="text-button" onClick={() => setShowCommentForm(false)} type="button">Cancel</button>
            </div>
            <textarea autoFocus className="theme-input guest-change-message" maxLength={2000} onChange={(e) => setCommentText(e.target.value)} placeholder="Leave precise feedback for this moment…" rows={3} value={commentText} />
            <div className="guest-inline-form-actions">
              <select aria-label="Comment category" className="theme-input" onChange={(e) => setCommentCategory(e.target.value)} value={commentCategory}>
                {COMMENT_CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
              <button className="primary-action" disabled={commentLoading || !commentText.trim()} type="submit">
                <Send className="h-4 w-4" /> {commentLoading ? "Posting…" : "Post comment"}
              </button>
            </div>
            {commentError ? <p className="form-error">{commentError}</p> : null}
          </form>
        ) : null}

        {changeSubmitted ? (
          <div className="guest-request-success"><CheckCircle className="h-4 w-4" /> Changes requested. The project owner can see your feedback in the portal.</div>
        ) : showChangesForm ? (
          <form className="guest-inline-review-form" onSubmit={handleRequestChanges}>
            <label className="form-field-label" htmlFor="project-change-message">What would you like changed?</label>
            <textarea autoFocus className="theme-input guest-change-message" id="project-change-message" maxLength={2000} onChange={(e) => setChangeMessage(e.target.value)} placeholder="For example: bring the vocal up in the second chorus…" rows={4} value={changeMessage} />
            <div className="guest-inline-form-actions">
              <button className="guest-secondary-action" onClick={() => { setChangeError(""); setShowChangesForm(false); }} type="button">Cancel</button>
              <button className="primary-action guest-request-submit" disabled={changeLoading || changeMessage.trim().length < 3} type="submit">
                <Send className="h-4 w-4" /> {changeLoading ? "Sending…" : "Send request"}
              </button>
            </div>
            {changeError ? <p className="form-error">{changeError}</p> : null}
          </form>
        ) : null}

        {activeFileComments.length > 0 ? (
          <div className="guest-player-comments" aria-label="Comments on this mix">
            {activeFileComments.map((comment) => (
              <button className={`guest-player-comment ${comment.status}`} key={comment.id} onClick={() => handleSelectComment(comment)} type="button">
                <span>{formatTime(comment.timestampSec)}</span>
                <p>{comment.text}</p>
                <small>{comment.authorRole === "admin" ? "Producer" : "Artist"}{comment.category ? ` · ${comment.category.replace(/_/g, " ")}` : ""}</small>
              </button>
            ))}
          </div>
        ) : null}
      </div>
    </section>
  ) : null;

  return (
    <div className="guest-review-shell">
      <div className="fixed inset-0 music-pattern-themed" aria-hidden="true" />
      <main className="guest-review-page">
        <header className="guest-review-brand">
          <a href="/" aria-label="Johnsonboibeats">
            <img src="/johnsonboibeats-logo.png" alt="Johnsonboibeats" />
          </a>
          <span>{isGuest ? "Private review link" : isAdmin ? "Admin · Project review" : "Artist · Project review"}</span>
        </header>

        {onBack ? (
          <button className="back-button" onClick={onBack} type="button">
            <ArrowLeft className="h-4 w-4" /> Back to projects
          </button>
        ) : null}

        <section className="guest-review-card guest-review-hero">
          <div className="guest-review-hero-layout">
            <div>
              <p className="eyebrow"><Music2 className="h-4 w-4" /> {data.artistName} · Project review</p>
              <h1>{data.title}</h1>
              <p>Listen, leave timestamped comments, request changes, approve the mix, pay securely, and download the finished file in one place.</p>
            </div>
            {priceCents > 0 ? (
              <div className="guest-review-price">
                <span>Project price</span>
                <strong>{"$"}{(priceCents / 100).toFixed(2)}</strong>
                <small>{balanceCents > 0 ? (balanceCents / 100).toFixed(2) + " balance due" : "Paid in full"} AUD</small>
              </div>
            ) : null}
          </div>
          <div className="guest-review-facts">
            <span>{statusLabel(status)}</span>
            {data.projectType ? <span>{PROJECT_TYPE_LABELS[data.projectType] ?? data.projectType}</span> : null}
            {data.bpm ? <span>{data.bpm} BPM</span> : null}
            {data.musicalKey ? <span>Key {data.musicalKey}</span> : null}
            <span>{files.length} delivery file{files.length === 1 ? "" : "s"}</span>
          </div>

          {isAdmin ? (
            <div className="guest-review-facts" style={{ marginTop: 12 }}>
              <div className="guest-link-inline">
                {canCreateReviewLink || reviewLinkUrl ? (
                  <>
                    {canCreateReviewLink ? (
                      <button className="guest-link-button" disabled={reviewLinkLoading} onClick={() => void handleCreateReviewLink()} title={reviewLinkUrl ? "Create a fresh private review link" : "Create and copy a private review link"} type="button">
                        <Link2 className="h-4 w-4" /> {reviewLinkLoading ? "Creating…" : reviewLinkUrl ? "Rotate link" : "Share private link"}
                      </button>
                    ) : null}
                    {reviewLinkUrl ? (
                      <div className="guest-link-inline-result">
                        <input aria-label="Guest review link" readOnly value={reviewLinkUrl} />
                        <button className="file-action-link" onClick={() => void handleCopyReviewLink()} type="button"><Copy className="h-4 w-4" /> {reviewLinkCopied ? "Copied" : "Copy"}</button>
                        <a className="file-action-link" href={reviewLinkUrl} rel="noreferrer" target="_blank"><Play className="h-4 w-4" /> Open</a>
                      </div>
                    ) : null}
                    {reviewLinkError ? <p className="form-error guest-link-error">{reviewLinkError}</p> : null}
                  </>
                ) : null}
              </div>
            </div>
          ) : null}

          {data.brief ? (
            <div className="project-brief" style={{ marginTop: 16 }}>
              <strong>Brief</strong>
              <p>{data.brief}</p>
            </div>
          ) : null}

          <div className="guest-review-facts" style={{ marginTop: 12 }}>
            <span>{hasTracks ? `${approvedTrackCount}/${tracks.length} tracks approved` : `${files.length} project files`}</span>
            <span>{openCommentCount} open comment{openCommentCount === 1 ? "" : "s"}</span>
            <span>{priceCents <= 0 ? "Payment not required" : isPaidInFull ? "Paid" : `$${(balanceCents / 100).toFixed(2)} balance`}</span>
          </div>

          {/* Status / approval actions */}
          {!hasTracks ? (
            <div className="status-actions" style={{ marginTop: 16 }}>
              {isClosed ? (
                <p className="review-closed-note">
                  {isDelivered
                    ? "This project has been delivered. Full downloads unlock after payment; review is closed."
                    : "This project is completed. Playback and full downloads are available; review is closed."}
                </p>
              ) : null}
              {isAdmin ? (
                <>
                  <select disabled={statusUpdating} onChange={(e) => handleStatusChange(e.target.value)} value={status}>
                    <option value="in_progress">In Progress</option>
                    <option value="ready_for_review">Ready for Review</option>
                    <option value="revisions">Revisions</option>
                    <option value="approved">Approved</option>
                    <option value="payment_required">Payment Required</option>
                    <option disabled={!paymentSatisfied} value="completed">Completed (paid)</option>
                    <option value="delivered">Delivered</option>
                  </select>
                  {canRequestChanges ? (
                    <button className="status-action-btn" disabled={statusUpdating} onClick={() => void handleApproveAndPay()} type="button">Request Revisions</button>
                  ) : null}
                </>
              ) : null}
              {statusError ? <p className="form-error">{statusError}</p> : null}
            </div>
          ) : (
            <div className="status-actions" style={{ marginTop: 16 }}>
              {isClosed ? (
                <>
                  <p className="review-closed-note">
                    {isDelivered
                      ? "This project has been delivered. Full downloads unlock after payment; review is closed."
                      : "This project is completed. Playback and full downloads are available; review is closed."}
                  </p>
                  {isAdmin ? (
                    <button className="status-action-btn" disabled={statusUpdating} onClick={() => handleStatusChange("in_progress")} type="button">Reopen Project</button>
                  ) : null}
                </>
              ) : allTracksApproved ? (
                <>
                  <p className="review-closed-note" style={{ color: "#b8ffba" }}>
                    All tracks approved — send the preview, then mark the project Delivered. Full payment will move it to Completed and unlock the full files.
                  </p>
                  {isAdmin ? (
                    <button className="primary-action" disabled={statusUpdating} onClick={() => handleStatusChange("delivered")} type="button">Mark Project Delivered</button>
                  ) : null}
                </>
              ) : isAdmin ? (
                <p className="review-closed-note">Track progress until the final preview is ready. After you send it, mark the project Delivered; full payment will then move it to Completed.</p>
              ) : null}
              {statusError ? <p className="form-error">{statusError}</p> : null}
            </div>
          )}

          {/* Payment banner */}
          {priceCents > 0 ? (
            <div className={`payment-banner ${isPaidInFull ? "paid" : balanceCents > 0 && showPayment ? "outstanding" : "pending"}`} style={{ marginTop: 16 }}>
              {isPaidInFull ? (
                <span><CheckCircle className="h-4 w-4" /> Payment complete</span>
              ) : balanceCents > 0 && showPayment ? (
                <div className="payment-banner-row">
                  <span>Outstanding balance: ${(balanceCents / 100).toFixed(2)}</span>
                  {!isAdmin && !isGuest ? (
                    <button className="primary-action" onClick={() => void handleApproveAndPay()} type="button"><CreditCard className="h-4 w-4" /> Complete Payment</button>
                  ) : isAdmin ? (
                    <>
                      <span className="payment-banner-note">Awaiting artist payment</span>
                      <button className="status-action-btn" disabled={paymentUpdating} onClick={() => void handleRecordExternalPayment()} type="button">
                        {paymentUpdating ? "Recording…" : "Record External Payment"}
                      </button>
                    </>
                  ) : null}
                </div>
              ) : (
                <span>Pending approval — payment available once tracks are approved</span>
              )}
              {paymentError ? <p className="form-error">{paymentError}</p> : null}
            </div>
          ) : null}
        </section>

        {/* The top-level preview card is only useful for single-track projects.
            Bundles already render a player card for every track. */}
        {!hasTracks ? previewSection : null}

        {/* Track sections (bundle) */}
        {hasTracks ? (
          <>
            {trackFileMap.map(({ track, index, trackFiles }) => {
              const trackStatus = track.status;
              const trackReviewEnabled = reviewEnabled;
              const trackCanRequestRevisions = trackReviewEnabled && (trackStatus === "done" || trackStatus === "approved" || trackStatus === "needs_revisions");
              const trackDefaultAudio = pickDefaultAudioFile(trackFiles, data.mainFileId, data.favouriteFileId);
              const activeTrackFile = activeFile?.trackIndex === index ? activeFile : undefined;
              const reviewFile = activeTrackFile ?? trackDefaultAudio;
              const reviewFileIsActive = Boolean(reviewFile) && activeFileId === reviewFile?.id;
              const trackComments = commentsForTrack(projectComments, index);
              const trackCommentFormOpen = showCommentForm && commentTrackIndex === index;

              return (
                <section className="guest-review-card guest-track-card" key={index}>
                  <div className="guest-track-heading">
                    <div>
                      <p className="eyebrow">Track {index + 1}</p>
                      <h2>{track.title}</h2>
                      {track.musicalKey || track.bpm ? (
                        <small>{track.musicalKey ? `Key: ${track.musicalKey}` : ""}{track.bpm ? ` · BPM: ${track.bpm}` : ""}</small>
                      ) : null}
                    </div>
                    <span className="track-status-pill" style={{ color: TRACK_STATUS_COLORS[trackStatus] ?? "var(--text-muted)", background: `${TRACK_STATUS_COLORS[trackStatus] ?? "var(--text-muted)"}1a` }}>
                      {TRACK_STATUS_LABELS[trackStatus] ?? trackStatus}
                    </span>
                  </div>

                  {reviewFile ? (
                    <div className="track-mini-player" style={{ marginTop: 16 }}>
                      <div className="soundcloud-player">
                        <button
                          aria-label={`${reviewFileIsActive && isPlaying ? "Pause" : "Play"} ${reviewFile.title}`}
                          className={`soundcloud-play-button ${reviewFileIsActive && isPlaying ? "playing" : ""}`}
                          disabled={!audioUrl || reviewFile.access !== "available"}
                          onClick={() => playFile(reviewFile)}
                          title={reviewFileIsActive && isPlaying ? "Pause track" : "Play track"}
                          type="button"
                        >
                          {reviewFileIsActive && isPlaying ? <Pause className="h-5 w-5" /> : <Play className="h-5 w-5" />}
                        </button>
                        <div className="soundcloud-player-content">
                          <div className="track-mini-player-head">
                            <div>
                              <p className="eyebrow">Reviewing now</p>
                              <strong>{reviewFile.title}</strong>
                              <small>{reviewFile.format.toUpperCase()}</small>
                            </div>
                          </div>
                          {reviewFileIsActive && audioUrl ? (
                            <>
                              <AudioMetaStrip bpm={track.bpm ?? displayBpm} musicalKey={track.musicalKey ?? displayKey} format={reviewFile.format} meta={fileMeta} loading={metaLoading && !fileMeta} />
                              <Waveform audioUrl={audioUrl} comments={commentMarkersFor(reviewFile.id, index)} currentTime={currentTime} duration={duration} height={120} isPlaying={isPlaying} onAddComment={trackReviewEnabled && canComment ? (ts) => startComment(ts, reviewFile, index) : undefined} onMeta={handleAudioMeta} onSeek={handleSeek} />
                            </>
                          ) : (
                            <div className="track-mini-player-placeholder"><span>Click play to load the waveform and comments</span></div>
                          )}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <p className="empty-state">No audio file for this track yet.</p>
                  )}

                  {trackReviewEnabled && canComment && reviewFile ? (
                    <div className="track-comment-thread">
                      <div className="track-comment-heading">
                        <div><p className="eyebrow">Timestamped feedback</p><h4>Track comments</h4></div>
                        {!trackCommentFormOpen ? (
                          <button className="primary-link" onClick={() => startComment(reviewFileIsActive ? Math.round(currentTime) : 0, reviewFile, index)} type="button">
                            <MessageSquare className="h-4 w-4" /> Add comment
                          </button>
                        ) : null}
                      </div>
                      {trackCommentFormOpen ? renderCommentForm(`Comment on ${track.title}`) : null}
                      {data.projectId ? (
                        <CommentList artistId={(data as any).artistId ?? (project?.artistId as any)} comments={trackComments} fileLabels={fileLabels} isAdmin={isAdmin} onSelectComment={handleSelectComment} projectId={data.projectId} />
                      ) : null}
                    </div>
                  ) : null}

                  {trackFiles.length > 0 ? (
                    <div className="track-files">
                      <p className="eyebrow">Files for this track</p>
                      <div className="guest-file-list">
                        {trackFiles.map((file) => {
                          const url = isGuest && token ? guestMediaUrl(file, token) : getFileUrl(file);
                          const playable = file.access === "available" && !!url && isAudioFormat(file.format, file.title);
                          const active = file.id === activeFileId;
                          const playingThis = active && isPlaying;
                          return (
                            <div key={file.id} className="guest-file-row">
                              <div>
                                <strong>{file.title}{file.isMainMix ? <span className="main-mix-badge">Main</span> : null}{file.isFavourite ? <span className="fav-badge">♥</span> : null}</strong>
                                <small>{file.format.toUpperCase()}</small>
                              </div>
                              <div className="guest-file-actions">
                                {playable ? (
                                  <button aria-label={`${playingThis ? "Pause" : "Play"} ${file.title}`} className={`file-play-btn soundcloud-play-button soundcloud-play-button-compact ${playingThis ? "playing" : ""}`} onClick={() => playFile(file)} title={playingThis ? "Pause track" : "Play track"} type="button">
                                    {playingThis ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
                                  </button>
                                ) : null}
                                {isAdmin && playable ? (
                                  <button className={`file-pin-btn ${file.isMainMix ? "pinned" : ""}`} disabled={pinningId === file.id || file.isMainMix} onClick={() => handlePinMainMix(file)} title="Set as main mix" type="button">
                                    <Pin className="h-4 w-4" /> {file.isMainMix ? "Main" : pinningId === file.id ? "Saving…" : "Main mix"}
                                  </button>
                                ) : null}
                                {playable ? (
                                  <button className={`file-fav-btn ${file.isFavourite ? "faved" : ""}`} disabled={favingId === file.id || file.isFavourite} onClick={() => handleFavourite(file)} title="Mark as your favourite" type="button">
                                    <Heart className="h-4 w-4" /> {file.isFavourite ? "Favourite" : favingId === file.id ? "Saving…" : "Favourite"}
                                  </button>
                                ) : null}
                                {isAdmin ? (
                                  <select aria-label={`Assign ${file.title} to a track`} className="track-file-assignment" disabled={assigningFileId === file.id} onChange={(e) => void handleFileAssignment(file.id, e.target.value === "" ? undefined : Number(e.target.value))} value={file.trackIndex ?? ""}>
                                    <option value="">Unassigned</option>
                                    {tracks.map((candidate, candidateIndex) => <option key={candidateIndex} value={candidateIndex}>Track {candidateIndex + 1}: {candidate.title}</option>)}
                                  </select>
                                ) : null}
                                {canDownloadBundle ? (
                                  <a className="file-action-link" download href={isGuest && token ? guestMediaUrl(file, token, "download") : getDownloadUrl(file)} rel="noreferrer"><Download className="h-4 w-4" /> Download</a>
                                ) : (
                                  <span className="guest-locked-file"><Lock className="h-4 w-4" /> Paid download</span>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ) : (
                    <p className="empty-state">No files matched to this track yet.</p>
                  )}

                  {trackReviewEnabled ? (
                    <div className="track-section-actions">
                      {isAdmin ? (
                        <select disabled={trackUpdating === index} value={trackStatus} onChange={(e) => void handleTrackAction(index, () => updateTrackStatus({ projectId: data.projectId as any, trackIndex: index, status: e.target.value }))}>
                          <option value="in_progress">In Progress</option>
                          <option value="done">Done</option>
                          <option value="approved">Approved</option>
                          <option value="needs_revisions">Needs Revisions</option>
                        </select>
                      ) : (
                        <>
                          {trackCanRequestRevisions ? (
                            <button className="status-action-btn" disabled={trackUpdating === index} type="button" onClick={() => void handleTrackAction(index, () => requestTrackRevisions({ projectId: data.projectId as any, trackIndex: index }))}>
                              <RotateCcw className="h-4 w-4" /> Request Revisions
                            </button>
                          ) : null}
                        </>
                      )}
                    </div>
                  ) : null}
                </section>
              );
            })}

            {isAdmin && unassignedFiles.length > 0 ? (
              <section className="guest-review-card guest-track-card">
                <div className="guest-track-heading">
                  <div><p className="eyebrow">Admin setup</p><h2>Unassigned bundle files</h2></div>
                  <span className="project-total">{unassignedFiles.length}</span>
                </div>
                <p className="review-closed-note">Confirm each file’s track before it appears in the artist’s bundle.</p>
                <div className="guest-file-list">
                  {unassignedFiles.map((file) => {
                    const suggestion = suggestTrackIndex(file.title, tracks);
                    return (
                      <div className="guest-file-row" key={file.id}>
                        <div>
                          <strong>{file.title}</strong>
                          <small>{suggestion === undefined ? "No reliable filename suggestion" : `Suggested: Track ${suggestion + 1} — ${tracks[suggestion].title}`}</small>
                        </div>
                        <div className="guest-file-actions">
                          <select aria-label={`Assign ${file.title} to a track`} className="track-file-assignment" disabled={assigningFileId === file.id} onChange={(e) => { if (e.target.value === "") return; void handleFileAssignment(file.id, Number(e.target.value)); }} value="">
                            <option value="">Choose track…</option>
                            {tracks.map((track, trackIndex) => <option key={trackIndex} value={trackIndex}>Track {trackIndex + 1}: {track.title}</option>)}
                          </select>
                          {suggestion !== undefined ? (
                            <button className="status-action-btn" disabled={assigningFileId === file.id} onClick={() => void handleFileAssignment(file.id, suggestion)} type="button">{assigningFileId === file.id ? "Assigning…" : "Use suggestion"}</button>
                          ) : null}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>
            ) : null}

            {trackError ? <p className="form-error">{trackError}</p> : null}
          </>
        ) : (
          /* Non-track project files */
          <section className="guest-review-card guest-track-card">
            <div className="guest-track-heading">
              <div><p className="eyebrow">Delivery</p><h2>Project files</h2></div>
              <span className="project-total">{files.length} {files.length === 1 ? "file" : "files"}</span>
            </div>
            {grouped.length > 0 ? (
              <div className="file-group-stack">
                {grouped.map(({ group, files: groupFilesList }) => {
                  const collapsed = collapsedGroups[group.id] ?? false;
                  return (
                    <div className="file-group" key={group.id}>
                      <div className={`file-group-heading-row ${collapsed ? "" : "open"}`}>
                        <button type="button" className={`file-group-heading ${collapsed ? "" : "open"}`} onClick={() => setCollapsedGroups((prev) => ({ ...prev, [group.id]: !collapsed }))}>
                          <div><strong>{group.label}</strong><small>{groupFilesList.length} {groupFilesList.length === 1 ? "file" : "files"}</small></div>
                          <span className="file-group-chevron">{collapsed ? "+" : "−"}</span>
                        </button>
                        {isAdmin ? (
                          <button type="button" className="group-zip-btn" disabled={zippingGroup === group.label || !canDownload || !groupFilesList.some((f) => f.access === "available" && (isGuest && token ? guestMediaUrl(f, token) : getFileUrl(f)))} onClick={() => canDownload ? handleZipGroup(group.label, groupFilesList) : undefined}>
                            <Download className="h-4 w-4" /> {zippingGroup === group.label ? "Zipping…" : "ZIP"}
                          </button>
                        ) : null}
                      </div>
                      {!collapsed ? (
                        <div className="guest-file-list">
                          {groupFilesList.map((file) => {
                            const url = isGuest && token ? guestMediaUrl(file, token) : getFileUrl(file);
                            const playable = file.access === "available" && !!url && isAudioFormat(file.format, file.title);
                            const active = file.id === activeFileId;
                            const playingThis = active && isPlaying;
                            return (
                              <div key={file.id} className="guest-file-row">
                                <div>
                                  <strong>{file.title}{file.isMainMix ? <span className="main-mix-badge">Main</span> : null}{file.isFavourite ? <span className="fav-badge">♥</span> : null}</strong>
                                  <small>{file.format.toUpperCase()}</small>
                                </div>
                                <div className="guest-file-actions">
                                  {playable ? (
                                    <button aria-label={`${playingThis ? "Pause" : "Play"} ${file.title}`} className={`file-play-btn soundcloud-play-button soundcloud-play-button-compact ${playingThis ? "playing" : ""}`} onClick={() => playFile(file)} title={playingThis ? "Pause track" : "Play track"} type="button">
                                      {playingThis ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
                                    </button>
                                  ) : null}
                                  {isAdmin && playable ? (
                                    <button className={`file-pin-btn ${file.isMainMix ? "pinned" : ""}`} disabled={pinningId === file.id || file.isMainMix} onClick={() => handlePinMainMix(file)} title="Set as main mix" type="button">
                                      <Pin className="h-4 w-4" /> {file.isMainMix ? "Main" : pinningId === file.id ? "Saving…" : "Main mix"}
                                    </button>
                                  ) : null}
                                  {playable ? (
                                    <button className={`file-fav-btn ${file.isFavourite ? "faved" : ""}`} disabled={favingId === file.id || file.isFavourite} onClick={() => handleFavourite(file)} title="Mark as your favourite" type="button">
                                      <Heart className="h-4 w-4" /> {file.isFavourite ? "Favourite" : favingId === file.id ? "Saving…" : "Favourite"}
                                    </button>
                                  ) : null}
                                  {isAdmin ? (
                                    <button className="file-action-link" onClick={() => void handleDelete(file.id)} type="button">Delete</button>
                                  ) : null}
                                  {canDownload ? (
                                    <a className="file-action-link" download href={isGuest && token ? guestMediaUrl(file, token, "download") : getDownloadUrl(file)} rel="noreferrer"><Download className="h-4 w-4" /> Download</a>
                                  ) : (
                                    <span className="guest-locked-file"><Lock className="h-4 w-4" /> Paid download</span>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="empty-state">{isAdmin ? "No files or stems added for this project yet." : "No files available for this project yet."}</p>
            )}
          </section>
        )}

        <footer className="guest-review-footer">
          <span>{isGuest ? "Private link · No account required" : "Johnsonboibeats"}</span>
          {isGuest ? <a href="/">Artist Portal login</a> : null}
        </footer>
      </main>
    </div>
  );
}
